import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Wrench, 
  Server, 
  AlertTriangle, 
  Trash2, 
  RefreshCw,
  CheckCircle,
  XCircle,
  Database,
  Bell,
  BellOff,
  FileText,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTools = () => {
  const [loading, setLoading] = useState(true);
  const [orphanedRouters, setOrphanedRouters] = useState([]);
  const [allRouters, setAllRouters] = useState([]);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [selectedRouters, setSelectedRouters] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState('bulk'); // 'bulk' or 'single'
  const [routerToDelete, setRouterToDelete] = useState(null);
  
  // Customer data fix states
  const [customerDataCheck, setCustomerDataCheck] = useState(null);
  const [fixingCustomerData, setFixingCustomerData] = useState(false);
  const [invoiceDiagnosis, setInvoiceDiagnosis] = useState(null);
  const [fixingInvoices, setFixingInvoices] = useState(false);
  
  // Export-Import states
  const [exportData, setExportData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [applyingFixes, setApplyingFixes] = useState(false);
  
  // Notification settings states
  const [notifSettings, setNotifSettings] = useState(null);
  const [savingNotif, setSavingNotif] = useState(false);
  
  // Bulk generate invoice states
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [generateParams, setGenerateParams] = useState({
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    due_date: '',
    skip_existing: true,
    only_active: false
  });

  // Duplicate PPPoE username checker states
  const [duplicatePPPoE, setDuplicatePPPoE] = useState(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  useEffect(() => {
    fetchRouters();
    checkCustomerData();
    diagnoseInvoices();
    fetchNotificationSettings();
    checkDuplicatePPPoEUsers();
  }, []);

  const checkCustomerData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/check-customer-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomerDataCheck(response.data.data);
      }
    } catch (error) {
      console.error('Error checking customer data:', error);
    }
  };

  const diagnoseInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/diagnose-invoice-customer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setInvoiceDiagnosis(response.data.data);
      }
    } catch (error) {
      console.error('Error diagnosing invoices:', error);
    }
  };

  const checkDuplicatePPPoEUsers = async () => {
    try {
      setCheckingDuplicates(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tools/duplicate-pppoe-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDuplicatePPPoE(response.data.data);
      }
    } catch (error) {
      console.error('Error checking duplicate PPPoE users:', error);
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleFixInvoiceCustomerIds = async () => {
    try {
      setFixingInvoices(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/fix-invoice-customer-ids`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Berhasil fix ${response.data.updated_count} invoice`);
        diagnoseInvoices(); // Refresh
      }
    } catch (error) {
      console.error('Error fixing invoice customer ids:', error);
      toast.error('Gagal fix invoice');
    } finally {
      setFixingInvoices(false);
    }
  };

  // Export invoice mismatch data
  const handleExportMismatch = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/export-invoice-customer-mismatch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setExportData(response.data);
        setShowExportModal(true);
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Gagal export data');
    } finally {
      setExportLoading(false);
    }
  };

  // Download as CSV
  const handleDownloadCSV = () => {
    if (!exportData?.data) return;
    
    const headers = ['invoice_id', 'invoice_number', 'customer_name', 'current_account_number', 'suggested_account_number', 'match_status'];
    const csvContent = [
      headers.join(','),
      ...exportData.data.map(row => 
        headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice_customer_mismatch_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  // Apply suggested fixes
  const handleApplySuggestedFixes = async () => {
    if (!exportData?.data) return;
    
    // Only apply fixes that have suggested_account_number and are not SUDAH_ADA
    const fixes = exportData.data
      .filter(row => row.suggested_account_number && row.match_status !== 'SUDAH_ADA')
      .map(row => ({
        invoice_id: row.invoice_id,
        customer_account_number: row.suggested_account_number
      }));
    
    if (fixes.length === 0) {
      toast.info('Tidak ada data yang perlu di-fix');
      return;
    }
    
    try {
      setApplyingFixes(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/import-invoice-customer-fix`, fixes, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Berhasil update ${response.data.updated} invoice`);
        setShowExportModal(false);
        diagnoseInvoices(); // Refresh
      }
    } catch (error) {
      console.error('Error applying fixes:', error);
      toast.error('Gagal apply fixes');
    } finally {
      setApplyingFixes(false);
    }
  };

  // Fetch notification settings
  const fetchNotificationSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/settings/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setNotifSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  // Save notification settings
  const handleSaveNotifSettings = async (newSettings) => {
    try {
      setSavingNotif(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/settings/notifications`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setNotifSettings({ ...notifSettings, ...newSettings });
        toast.success('Pengaturan notifikasi berhasil disimpan');
      }
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSavingNotif(false);
    }
  };

  // Bulk generate invoices
  const handleBulkGenerateInvoices = async () => {
    try {
      setGenerateLoading(true);
      setGenerateResult(null);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/api/admin/bulk-generate-invoices`, generateParams, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setGenerateResult(response.data);
        toast.success(`Berhasil generate ${response.data.generated_count} invoice`);
        diagnoseInvoices(); // Refresh invoice count
      }
    } catch (error) {
      console.error('Error generating invoices:', error);
      toast.error(error.response?.data?.detail || 'Gagal generate invoice');
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleFixCustomerAccountNumbers = async () => {
    try {
      setFixingCustomerData(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/fix-customer-account-numbers`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Berhasil fix ${response.data.updated_count} pelanggan tanpa ID Pelanggan`);
        checkCustomerData(); // Refresh data
      }
    } catch (error) {
      console.error('Error fixing customer data:', error);
      toast.error('Gagal fix data pelanggan');
    } finally {
      setFixingCustomerData(false);
    }
  };

  const fetchRouters = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/mikrotiks`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const routers = response.data.data;
        setAllRouters(routers);
        
        // Filter orphaned routers (without tenant_id)
        const orphaned = routers.filter(router => !router.tenant_id);
        setOrphanedRouters(orphaned);
      }
    } catch (error) {
      console.error('Error fetching routers:', error);
      toast.error('Gagal memuat data router');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRouter = (routerId) => {
    setSelectedRouters(prev => {
      if (prev.includes(routerId)) {
        return prev.filter(id => id !== routerId);
      } else {
        return [...prev, routerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedRouters.length === orphanedRouters.length) {
      setSelectedRouters([]);
    } else {
      setSelectedRouters(orphanedRouters.map(r => r.id));
    }
  };

  const handleCleanupSelected = async () => {
    if (selectedRouters.length === 0) {
      toast.error('Pilih minimal 1 router untuk dihapus');
      return;
    }

    setDeleteType('bulk');
    setShowDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setCleanupLoading(true);
      setShowDeleteModal(false);
      const token = localStorage.getItem('token');
      let successCount = 0;
      let failCount = 0;

      for (const routerId of selectedRouters) {
        try {
          await axios.delete(`${API_URL}/api/mikrotiks/${routerId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to delete router ${routerId}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} router berhasil dihapus`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} router gagal dihapus`);
      }

      setSelectedRouters([]);
      fetchRouters();
    } catch (error) {
      console.error('Error cleaning up routers:', error);
      toast.error('Gagal menghapus router');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleDeleteSingle = async (router) => {
    setRouterToDelete(router);
    setDeleteType('single');
    setShowDeleteModal(true);
  };

  const confirmSingleDelete = async () => {
    try {
      setShowDeleteModal(false);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/mikrotiks/${routerToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Router "${routerToDelete.router_name}" berhasil dihapus`);
      fetchRouters();
    } catch (error) {
      console.error('Error deleting router:', error);
      toast.error('Gagal menghapus router');
    } finally {
      setRouterToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 dark:text-indigo-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Admin Tools</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">System maintenance and cleanup utilities</p>
              </div>
            </div>
          </div>
          <button
            onClick={fetchRouters}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700/50"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="text-sm font-medium">Refresh</span>
          </button>
        </div>

        {/* Customer Data Fix Card */}
        <Card className="border-0 shadow-md bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-indigo-100 rounded-lg dark:bg-indigo-900/40">
                  <Database className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Fix Data Pelanggan</h3>
                  <p className="text-sm text-slate-600 mb-3 dark:text-slate-300">Perbaiki pelanggan yang tidak memiliki ID Pelanggan (account_number)</p>
                  
                  {customerDataCheck && (
                    <div className="flex flex-wrap gap-4 text-sm mb-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span><strong>{customerDataCheck.customers_with_account_number}</strong> punya ID</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span><strong>{customerDataCheck.customers_without_account_number}</strong> tanpa ID</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Server className="w-4 h-4 text-blue-500" />
                        <span>Total: <strong>{customerDataCheck.total_customers}</strong></span>
                      </div>
                    </div>
                  )}
                  
                  {customerDataCheck?.sample_without_account?.length > 0 && (
                    <div className="text-xs text-slate-500 mb-3 dark:text-slate-400">
                      Contoh tanpa ID: {customerDataCheck.sample_without_account.map(c => c.name).join(', ')}
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={handleFixCustomerAccountNumbers}
                disabled={fixingCustomerData || (customerDataCheck?.customers_without_account_number === 0)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  customerDataCheck?.customers_without_account_number > 0
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {fixingCustomerData ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Fixing...</span>
                  </>
                ) : customerDataCheck?.customers_without_account_number > 0 ? (
                  <>
                    <Wrench className="w-4 h-4" />
                    <span>Fix {customerDataCheck.customers_without_account_number} Pelanggan</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Semua Data OK</span>
                  </>
                )}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Duplicate PPPoE Username Checker Card */}
        <Card className="border-0 shadow-md bg-gradient-to-r from-red-50 to-pink-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-lg dark:bg-red-900/40">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Duplikat Username PPPoE</h3>
                  <p className="text-sm text-slate-600 mb-3 dark:text-slate-300">
                    Deteksi pelanggan dengan username PPPoE (mikrotik_user) yang sama. 
                    Username PPPoE harus unik untuk setiap pelanggan.
                  </p>
                  
                  {checkingDuplicates ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Memeriksa duplikat...</span>
                    </div>
                  ) : duplicatePPPoE ? (
                    <>
                      <div className="flex flex-wrap gap-4 text-sm mb-3">
                        <div className="flex items-center gap-2">
                          {duplicatePPPoE.total_duplicate_usernames > 0 ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                          <span>
                            <strong>{duplicatePPPoE.total_duplicate_usernames}</strong> username duplikat
                          </span>
                        </div>
                        {duplicatePPPoE.total_affected_customers > 0 && (
                          <div className="flex items-center gap-2">
                            <Server className="w-4 h-4 text-orange-500" />
                            <span>
                              <strong>{duplicatePPPoE.total_affected_customers}</strong> pelanggan terdampak
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {duplicatePPPoE.duplicates?.length > 0 && (
                        <div className="mt-3 space-y-3">
                          <p className="text-sm font-medium text-red-700 dark:text-red-300">Username duplikat yang ditemukan:</p>
                          <div className="max-h-60 overflow-y-auto space-y-2">
                            {duplicatePPPoE.duplicates.map((dup, idx) => (
                              <div key={idx} className="p-3 bg-white rounded-lg border border-red-200 dark:bg-slate-800">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="destructive" className="text-xs">
                                    {dup._id}
                                  </Badge>
                                  <span className="text-xs text-slate-500 dark:text-slate-400">
                                    ({dup.count} pelanggan)
                                  </span>
                                </div>
                                <div className="space-y-1">
                                  {dup.customers.map((cust, cidx) => (
                                    <div key={cidx} className="text-xs flex items-center justify-between bg-slate-50 p-2 rounded dark:bg-slate-900">
                                      <div>
                                        <span className="font-medium">{cust.name}</span>
                                        <span className="text-slate-500 ml-2 dark:text-slate-400">ID: {cust.account_number}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-slate-500 dark:text-slate-400">{cust.service_plan}</span>
                                        {cust.is_active ? (
                                          <Badge variant="success" className="text-[10px] px-1.5 py-0">Aktif</Badge>
                                        ) : (
                                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Non-Aktif</Badge>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <p className="text-xs text-red-600 mt-2 dark:text-red-400">
                                  ⚠️ Ubah username PPPoE salah satu pelanggan agar tidak duplikat
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
              
              <button
                onClick={checkDuplicatePPPoEUsers}
                disabled={checkingDuplicates}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all bg-red-600 hover:bg-red-700 text-white"
              >
                {checkingDuplicates ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Checking...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Cek Ulang</span>
                  </>
                )}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Invoice-Customer Match Diagnosis Card */}
        <Card className="border-0 shadow-md bg-gradient-to-r from-orange-50 to-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-orange-100 rounded-lg dark:bg-orange-900/40">
                  <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Fix Invoice Customer ID</h3>
                  <p className="text-sm text-slate-600 mb-3 dark:text-slate-300">Perbaiki invoice yang belum memiliki ID Pelanggan (customer_account_number)</p>
                  
                  {invoiceDiagnosis && (
                    <div className="flex flex-wrap gap-4 text-sm mb-3">
                      <div className="flex items-center gap-2">
                        <Server className="w-4 h-4 text-blue-500" />
                        <span><strong>{invoiceDiagnosis.total_invoices || 0}</strong> invoice</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span><strong>{invoiceDiagnosis.total_customer_ids_in_customers || 0}</strong> pelanggan</span>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Klik &quot;Fix Semua Invoice&quot; untuk menambahkan ID Pelanggan ke semua invoice berdasarkan data pelanggan
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleFixInvoiceCustomerIds}
                disabled={fixingInvoices}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all bg-orange-600 hover:bg-orange-700 text-white"
              >
                {fixingInvoices ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Fixing...</span>
                  </>
                ) : (
                  <>
                    <Wrench className="w-4 h-4" />
                    <span>Fix Semua Invoice</span>
                  </>
                )}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Export & Review Tool - NEW */}
        <Card className="border-0 shadow-md bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-100 rounded-lg dark:bg-green-900/40">
                  <Database className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Export & Review Invoice Data</h3>
                  <p className="text-sm text-slate-600 mb-3 dark:text-slate-300">
                    Export data invoice untuk review manual, lihat suggested match, lalu apply fixes
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Tool ini cocok untuk memeriksa data sebelum di-fix secara massal
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleExportMismatch}
                disabled={exportLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all bg-green-600 hover:bg-green-700 text-white"
              >
                {exportLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4" />
                    <span>Export & Review</span>
                  </>
                )}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings Card */}
        <Card className="border-0 shadow-md bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-100 rounded-lg dark:bg-purple-900/40">
                  {notifSettings?.whatsapp_enabled || notifSettings?.smtp_enabled ? (
                    <Bell className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  ) : (
                    <BellOff className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Pengaturan Notifikasi</h3>
                  <p className="text-sm text-slate-600 mb-3 dark:text-slate-300">
                    Aktifkan/nonaktifkan pengiriman WhatsApp dan Email
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant={notifSettings?.whatsapp_enabled ? "default" : "secondary"}>
                      WhatsApp: {notifSettings?.whatsapp_enabled ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                    <Badge variant={notifSettings?.smtp_enabled ? "default" : "secondary"}>
                      Email: {notifSettings?.smtp_enabled ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                    <Badge variant={notifSettings?.auto_reminder_enabled ? "default" : "secondary"}>
                      Auto Reminder: {notifSettings?.auto_reminder_enabled ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleSaveNotifSettings({
                    whatsapp_enabled: false,
                    smtp_enabled: false,
                    auto_reminder_enabled: false
                  })}
                  disabled={savingNotif}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all bg-red-600 hover:bg-red-700 text-white"
                >
                  <BellOff className="w-4 h-4" />
                  <span>Nonaktifkan Semua</span>
                </button>
                <button
                  onClick={() => handleSaveNotifSettings({
                    whatsapp_enabled: true,
                    smtp_enabled: true,
                    auto_reminder_enabled: true
                  })}
                  disabled={savingNotif}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all bg-green-600 hover:bg-green-700 text-white"
                >
                  <Bell className="w-4 h-4" />
                  <span>Aktifkan Semua</span>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Generate Invoice Card */}
        <Card className="border-0 shadow-md bg-gradient-to-r from-yellow-50 to-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-yellow-100 rounded-lg dark:bg-yellow-900/40">
                  <FileText className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Generate Invoice Massal</h3>
                  <p className="text-sm text-slate-600 mb-3 dark:text-slate-300">
                    Buat invoice untuk semua pelanggan sekaligus (tanpa kirim notifikasi)
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Cocok untuk migrasi data atau generate tagihan bulanan
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setShowGenerateModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                <FileText className="w-4 h-4" />
                <span>Generate Invoice</span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1 dark:text-slate-400">Total Routers</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{allRouters.length}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg dark:bg-blue-900/30">
                  <Server className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1 dark:text-slate-400">Orphaned Routers</p>
                  <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{orphanedRouters.length}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg dark:bg-orange-900/30">
                  <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1 dark:text-slate-400">Production Routers</p>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400">{allRouters.length - orphanedRouters.length}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg dark:bg-green-900/30">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Orphaned Routers Management */}
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b border-slate-200 bg-gradient-to-r from-orange-50 to-red-50 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg dark:bg-orange-900/40">
                  <Database className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <CardTitle className="text-lg">Orphaned Routers Cleanup</CardTitle>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Routers without tenant assignment (testing/legacy data)</p>
                </div>
              </div>
              {orphanedRouters.length > 0 && (
                <Badge variant="destructive" className="text-sm">
                  {orphanedRouters.length} Orphaned
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {orphanedRouters.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2 dark:text-slate-100">All Clean!</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">No orphaned routers found. System is healthy.</p>
              </div>
            ) : (
              <>
                {/* Bulk Actions */}
                <div className="flex items-center justify-between mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 dark:bg-slate-900 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedRouters.length === orphanedRouters.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-indigo-600 rounded dark:text-indigo-400"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {selectedRouters.length > 0 
                        ? `${selectedRouters.length} router dipilih` 
                        : 'Pilih semua'}
                    </span>
                  </div>
                  {selectedRouters.length > 0 && (
                    <button
                      onClick={handleCleanupSelected}
                      disabled={cleanupLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {cleanupLoading ? 'Menghapus...' : 'Hapus Terpilih'}
                      </span>
                    </button>
                  )}
                </div>

                {/* Router List */}
                <div className="space-y-3">
                  {orphanedRouters.map((router) => (
                    <div
                      key={router.id}
                      className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg hover:border-orange-300 transition-all dark:bg-slate-800 dark:border-slate-700"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedRouters.includes(router.id)}
                          onChange={() => handleSelectRouter(router.id)}
                          className="w-4 h-4 text-indigo-600 rounded dark:text-indigo-400"
                        />
                        <div className="flex items-center gap-3 flex-1">
                          <div className="p-2 bg-orange-50 rounded dark:bg-orange-900/30">
                            <Server className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-slate-900 dark:text-slate-100">{router.router_name || 'Unknown Router'}</h4>
                              <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300">
                                No Tenant
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                              <span className="font-mono">{router.host}</span>
                              {router.location && (
                                <>
                                  <span>•</span>
                                  <span>{router.location}</span>
                                </>
                              )}
                              {router.created_at && (
                                <>
                                  <span>•</span>
                                  <span>Created: {new Date(router.created_at).toLocaleDateString('id-ID')}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteSingle(router)}
                        className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all dark:text-red-400 dark:hover:bg-red-900/30"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="text-sm font-medium">Delete</span>
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="border-0 shadow-md bg-blue-50 dark:bg-blue-900/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5 dark:text-blue-400" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">About Orphaned Routers</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>Orphaned routers</strong> are routers without tenant assignment (tenant_id = null)</li>
                  <li>• Usually from testing, development, or migration process</li>
                  <li>• Safe to delete if confirmed not in use</li>
                  <li>• Production routers with tenant_id will not appear here</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl dark:bg-slate-800">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-50 rounded-full dark:bg-red-900/30">
                    <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                      {deleteType === 'bulk' ? 'Hapus Router Terpilih?' : 'Hapus Router?'}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Tindakan ini tidak dapat dibatalkan</p>
                  </div>
                </div>

                {/* Content */}
                <div className="mb-6">
                  {deleteType === 'bulk' ? (
                    <>
                      <p className="text-slate-700 mb-4 dark:text-slate-200">
                        Anda akan menghapus <strong className="text-red-600 dark:text-red-400">{selectedRouters.length} router</strong> dari sistem:
                      </p>
                      <div className="bg-slate-50 rounded-lg p-4 max-h-48 overflow-y-auto space-y-2 dark:bg-slate-900">
                        {orphanedRouters
                          .filter(r => selectedRouters.includes(r.id))
                          .map((router, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <Server className="w-4 h-4 text-slate-400 flex-shrink-0 dark:text-slate-500" />
                              <span className="font-medium text-slate-700 dark:text-slate-200">{router.router_name}</span>
                              <span className="text-slate-400 dark:text-slate-500">•</span>
                              <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{router.host}</span>
                            </div>
                          ))}
                      </div>
                    </>
                  ) : (
                    routerToDelete && (
                      <>
                        <p className="text-slate-700 mb-4 dark:text-slate-200">
                          Anda akan menghapus router berikut dari sistem:
                        </p>
                        <div className="bg-slate-50 rounded-lg p-4 border-l-4 border-red-500 dark:bg-slate-900">
                          <div className="flex items-start gap-3">
                            <Server className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5 dark:text-red-400" />
                            <div>
                              <p className="font-semibold text-slate-900 dark:text-slate-100">{routerToDelete.router_name}</p>
                              <p className="text-sm text-slate-500 font-mono dark:text-slate-400">{routerToDelete.host}</p>
                              {routerToDelete.location && (
                                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">📍 {routerToDelete.location}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )
                  )}

                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/30">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5 dark:text-amber-400" />
                      <div className="text-xs text-amber-800">
                        <strong>Peringatan:</strong> Data router akan dihapus permanen dan tidak dapat dikembalikan.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setRouterToDelete(null);
                    }}
                    className="flex-1 px-4 py-2.5 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium transition-all dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
                  >
                    Batal
                  </button>
                  <button
                    onClick={deleteType === 'bulk' ? confirmBulkDelete : confirmSingleDelete}
                    disabled={cleanupLoading}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    {cleanupLoading ? 'Menghapus...' : 'Ya, Hapus Router'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Review Modal */}
        {showExportModal && exportData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col dark:bg-slate-800">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">Export & Review Invoice Data</h3>
                    <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">Review data sebelum apply fixes</p>
                  </div>
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="text-slate-400 hover:text-slate-600 dark:text-slate-500"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              {/* Stats */}
              <div className="p-4 bg-slate-50 border-b grid grid-cols-5 gap-4 dark:bg-slate-900">
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{exportData.stats?.total_invoices || 0}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Total Invoice</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{exportData.stats?.sudah_ada || 0}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Sudah Ada ID</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{exportData.stats?.match_by_id || 0}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Match by ID</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{exportData.stats?.match_by_name || 0}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Match by Nama</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{exportData.stats?.tidak_ditemukan || 0}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Tidak Ditemukan</div>
                </div>
              </div>
              
              {/* Table */}
              <div className="flex-1 overflow-auto p-4">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0 dark:bg-slate-800">
                    <tr>
                      <th className="text-left p-2">No. Invoice</th>
                      <th className="text-left p-2">Nama Pelanggan</th>
                      <th className="text-left p-2">ID Sekarang</th>
                      <th className="text-left p-2">Suggested ID</th>
                      <th className="text-left p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exportData.data?.slice(0, 100).map((row, idx) => (
                      <tr key={idx} className={`border-b ${row.match_status === 'TIDAK_DITEMUKAN' ? 'bg-red-50' : ''}`}>
                        <td className="p-2 font-mono text-xs">{row.invoice_number}</td>
                        <td className="p-2">{row.customer_name}</td>
                        <td className="p-2 font-mono text-xs">{row.current_account_number || '-'}</td>
                        <td className="p-2 font-mono text-xs text-green-600 dark:text-green-400">{row.suggested_account_number || '-'}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            row.match_status === 'SUDAH_ADA' ? 'bg-green-100 text-green-700' :
                            row.match_status === 'MATCH_BY_ID' ? 'bg-blue-100 text-blue-700' :
                            row.match_status === 'MATCH_BY_NAME' ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {row.match_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {exportData.data?.length > 100 && (
                  <p className="text-center text-sm text-slate-500 mt-4 dark:text-slate-400">
                    Menampilkan 100 dari {exportData.data.length} data. Download CSV untuk melihat semua.
                  </p>
                )}
              </div>
              
              {/* Actions */}
              <div className="p-4 border-t bg-slate-50 flex gap-3 justify-end dark:bg-slate-900">
                <button
                  onClick={handleDownloadCSV}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100 font-medium dark:border-slate-600 dark:hover:bg-slate-700"
                >
                  📥 Download CSV
                </button>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100 font-medium dark:border-slate-600 dark:hover:bg-slate-700"
                >
                  Tutup
                </button>
                <button
                  onClick={handleApplySuggestedFixes}
                  disabled={applyingFixes || (exportData.stats?.match_by_id + exportData.stats?.match_by_name) === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {applyingFixes ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Applying...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Apply {(exportData.stats?.match_by_id || 0) + (exportData.stats?.match_by_name || 0)} Fixes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Generate Invoice Modal */}
        {showGenerateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl dark:bg-slate-800">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">Generate Invoice Massal</h3>
                    <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">Buat invoice untuk semua pelanggan</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowGenerateModal(false);
                      setGenerateResult(null);
                    }}
                    className="text-slate-400 hover:text-slate-600 dark:text-slate-500"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {!generateResult ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">Bulan</label>
                        <select
                          value={generateParams.period_month}
                          onChange={(e) => setGenerateParams({...generateParams, period_month: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:border-slate-600"
                        >
                          {[...Array(12)].map((_, i) => (
                            <option key={i+1} value={i+1}>
                              {new Date(2024, i, 1).toLocaleString('id-ID', { month: 'long' })}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">Tahun</label>
                        <select
                          value={generateParams.period_year}
                          onChange={(e) => setGenerateParams({...generateParams, period_year: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:border-slate-600"
                        >
                          {[2023, 2024, 2025, 2026].map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">Tanggal Jatuh Tempo</label>
                      <input
                        type="date"
                        value={generateParams.due_date}
                        onChange={(e) => setGenerateParams({...generateParams, due_date: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:border-slate-600"
                      />
                      <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Kosongkan untuk default akhir bulan</p>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={generateParams.skip_existing}
                          onChange={(e) => setGenerateParams({...generateParams, skip_existing: e.target.checked})}
                          className="rounded border-slate-300 dark:border-slate-600"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-200">Skip pelanggan yang sudah punya invoice periode ini</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={generateParams.only_active}
                          onChange={(e) => setGenerateParams({...generateParams, only_active: e.target.checked})}
                          className="rounded border-slate-300 dark:border-slate-600"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-200">Hanya pelanggan aktif</span>
                      </label>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 dark:bg-yellow-900/30">
                      <p className="text-sm text-yellow-800">
                        <strong>Catatan:</strong> Invoice akan di-generate TANPA mengirim notifikasi WhatsApp/Email.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center py-4">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3" />
                      <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Generate Selesai!</h4>
                      <p className="text-slate-600 dark:text-slate-300">Periode: {generateResult.period}</p>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-green-50 rounded-lg p-3 text-center dark:bg-green-900/30">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{generateResult.generated_count}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-300">Berhasil</div>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-3 text-center dark:bg-yellow-900/30">
                        <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{generateResult.skipped_count}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-300">Dilewati</div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 text-center dark:bg-red-900/30">
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">{generateResult.error_count}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-300">Error</div>
                      </div>
                    </div>
                    
                    {generateResult.skipped?.length > 0 && (
                      <div className="bg-slate-50 rounded-lg p-3 max-h-32 overflow-auto dark:bg-slate-900">
                        <p className="text-xs font-medium text-slate-700 mb-1 dark:text-slate-200">Dilewati:</p>
                        {generateResult.skipped.slice(0, 5).map((s, i) => (
                          <p key={i} className="text-xs text-slate-500 dark:text-slate-400">{s.customer}: {s.reason}</p>
                        ))}
                        {generateResult.skipped.length > 5 && (
                          <p className="text-xs text-slate-400 dark:text-slate-500">...dan {generateResult.skipped.length - 5} lainnya</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t bg-slate-50 flex gap-3 justify-end dark:bg-slate-900">
                <button
                  onClick={() => {
                    setShowGenerateModal(false);
                    setGenerateResult(null);
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100 font-medium dark:border-slate-600 dark:hover:bg-slate-700"
                >
                  Tutup
                </button>
                {!generateResult && (
                  <button
                    onClick={handleBulkGenerateInvoices}
                    disabled={generateLoading}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium disabled:opacity-50 flex items-center gap-2"
                  >
                    {generateLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4" />
                        <span>Generate Sekarang</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTools;
